import LegalLayout, { Section, Bullets } from "@/components/LegalLayout";
import { LEGAL_VERSIONS, PLACEHOLDERS, PROHIBITED_INFORMATION } from "@/constants/legalContent";
import { AlertTriangle } from "lucide-react";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Use"
      intro="These Terms of Use explain how FindRxStaff works, what we do, what we do not do, and what we expect from the pharmacies and pharmacy professionals who use the platform. Please read them carefully. We may update these terms from time to time and will post the updated version here with a new Last Updated date."
    >
      <p className="text-sm text-slate-500" data-testid="terms-version">
        Terms of Use version {LEGAL_VERSIONS.terms}
      </p>

      <Section heading="1. Platform purpose">
        <p>
          FindRxStaff provides a technology platform that allows pharmacies and pharmacy professionals to
          discover, communicate with, and potentially arrange staffing opportunities. The platform may
          facilitate shift discovery, availability matching, staffing requests, communication between users,
          and shift confirmation.
        </p>
        <p>
          FindRxStaff is not a traditional employment or staffing agency unless expressly stated otherwise in
          a separate written agreement. We do not place workers on assignment on our own behalf.
        </p>
      </Section>

      <Section heading="2. FindRxStaff is not the employer">
        <p>Unless expressly agreed otherwise in writing, FindRxStaff does not employ:</p>
        <Bullets items={["Pharmacists", "Pharmacy technicians", "Pharmacy clerks", "Pharmacy owners", "Pharmacy employees", "Other users of the platform"]} />
        <p>
          Creating an account or using FindRxStaff does not, by itself, create an employment relationship
          between FindRxStaff and any user. FindRxStaff also does not generally:
        </p>
        <Bullets
          items={[
            "Supervise workers or control how work is performed",
            "Set employment terms",
            "Determine wages or schedules on behalf of users",
            "Provide payroll services or withhold taxes",
            "Provide employment benefits",
            "Guarantee employment or continued engagement",
          ]}
        />
        <p>
          The actual legal relationship between a pharmacy and a worker — including employment
          classification — depends on applicable law and the arrangement the parties themselves make. Users
          are responsible for determining and complying with the requirements that apply to their
          arrangement.
        </p>
      </Section>

      <Section heading="3. Pharmacy responsibilities">
        <p>Pharmacies are responsible for independently verifying:</p>
        <Bullets
          items={[
            "Worker identity",
            "Professional license and license status",
            "Technician registration or certification, where applicable",
            "Experience, references and skills",
            "Availability",
            "Eligibility to work",
            "Any other qualification required by the pharmacy or by applicable law",
          ]}
        />
        <p>Pharmacies are also responsible for:</p>
        <Bullets
          items={[
            "Providing accurate shift information",
            "Providing accurate compensation information",
            "Confirming working hours and job responsibilities",
            "Confirming workplace conditions",
            "Confirming payment arrangements",
            "Complying with applicable employment, labor and pharmacy laws",
          ]}
        />
      </Section>

      <Section heading="4. Worker responsibilities">
        <p>Workers are responsible for independently verifying:</p>
        <Bullets
          items={[
            "Pharmacy identity and location",
            "Shift date and hours",
            "Compensation, payment method and payment timing",
            "Job responsibilities",
            "Workplace conditions",
            "Required qualifications",
            "Any other term of the proposed arrangement",
          ]}
        />
        <p>
          Workers should independently determine whether they are properly licensed, registered, certified,
          insured or otherwise qualified to perform the work, and must comply with all applicable licensing,
          registration, certification and professional requirements in the state where the work is performed.
        </p>
      </Section>

      <Section heading="5. Compensation and payment">
        <p>Unless a future feature explicitly states otherwise, FindRxStaff does not:</p>
        <Bullets
          items={[
            "Determine or guarantee compensation",
            "Collect compensation or hold funds",
            "Process payments or pay workers",
            "Guarantee payment",
            "Enforce payment agreements between users",
          ]}
        />
        <p>
          Pharmacies and workers are responsible for independently agreeing on the hourly rate, total
          compensation, payment method, payment timing, reimbursements, taxes and any other financial
          arrangement. We strongly recommend that both parties confirm compensation and payment terms in
          writing before accepting or beginning a shift.
        </p>
      </Section>

      <Section heading="6. Shift confirmation">
        <p>
          Posting a shift does not guarantee that a pharmacy will receive interested workers. Expressing
          interest does not guarantee that a worker will be selected. A pharmacy requesting a worker does not
          by itself confirm the shift.
        </p>
        <p>
          A shift is shown as <strong>Confirmed</strong> only after the required parties have completed the
          confirmation process in the platform. The platform distinguishes the statuses Open, Interested,
          Requested, Confirmed, Completed and Cancelled so both sides can see exactly where an opportunity
          stands.
        </p>
      </Section>

      <Section heading="7. No guarantee of staffing outcomes">
        <p>FindRxStaff does not guarantee:</p>
        <Bullets
          items={[
            "That a shift will be filled",
            "That a worker will accept a shift",
            "That a pharmacy will accept a worker",
            "That a user will receive a particular pay rate",
            "That a user will receive a particular number of opportunities",
            "That another user will appear or remain available",
            "That a user will perform as expected",
          ]}
        />
        <p>The platform facilitates connections; the outcome of those connections is up to the users.</p>
      </Section>

      <Section heading="8. User-generated information">
        <p>
          Users are responsible for the information they submit. FindRxStaff does not guarantee that
          user-provided information is accurate, complete, current, authentic or error-free. This applies to
          profiles, licenses, certifications, experience, availability, pay, pharmacy information, shift
          information and any other user-generated content.
        </p>
        <p>
          Verification status is displayed as Unverified, Verification pending or Verified. Verification is
          automatic and reflects platform activity only: a worker moves to Verification pending when their
          first shift is confirmed, and to Verified once a pharmacy reviews that completed shift. It is not a
          licence check. Creating an account does not make a user verified, and users remain responsible for
          independently verifying identity, licences, registrations and certifications.
        </p>
      </Section>

      <Section heading="9. Confidential information and PHI">
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900" data-testid="terms-phi-warning">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Do not share confidential or patient information</p>
            <p className="mt-1.5 text-sm">
              FindRxStaff is a staffing coordination platform. It is not intended for submitting, storing or
              exchanging patient health information or other protected health information, and must not be
              used to communicate about individual patients.
            </p>
          </div>
        </div>
        <p>Users must not post or submit:</p>
        <Bullets items={PROHIBITED_INFORMATION} />
        <p>
          This applies to shift descriptions, profile descriptions, notes, comments, reports and support
          requests. We may remove content and suspend accounts that violate this section.
        </p>
      </Section>

      <Section heading="10. Disputes between users">
        <p>
          Users are responsible for their own arrangements. Disputes between users may involve payment,
          schedules, cancellations, no-shows, work performance, workplace conditions, professional conduct,
          licensing, credentials, employment classification, taxes or other agreements.
        </p>
        <p>
          FindRxStaff is a platform that connects users and is not automatically a party to agreements
          between users. To the extent permitted by applicable law, we are not responsible for the acts,
          omissions or agreements of users, although we may act on reports of prohibited conduct on the
          platform.
        </p>
      </Section>

      <Section heading="11. Acceptable use">
        <p>
          Your use of FindRxStaff is also governed by our Acceptable Use Policy, which prohibits, among other
          things, misrepresenting credentials, posting fraudulent shifts, harassment, unlawful
          discrimination, spam, scraping and attempts to gain unauthorized access.
        </p>
      </Section>

      <Section heading="12. Free access and future changes">
        <p>
          FindRxStaff is currently free to use. Free access does not mean the platform guarantees any
          outcome, and does not mean that using the platform is without risk. We may change pricing,
          features or availability in the future, subject to updated terms and appropriate notice.
        </p>
      </Section>

      <Section heading="13. Account suspension and termination">
        <p>
          We may suspend or terminate accounts that violate these terms, our Acceptable Use Policy or
          applicable law, or where necessary to protect users or the platform. You may delete your account at
          any time from your settings; some records may be retained where reasonably necessary for legal,
          security, dispute-resolution or record-keeping purposes.
        </p>
      </Section>

      <Section heading="14. Changes to these terms">
        <p>
          We may update these Terms of Use from time to time. When we do, we will update the version number
          and the Last Updated date on this page, and where appropriate we will ask users to accept the
          updated version. Historical acceptance records are retained.
        </p>
      </Section>

      <Section heading="15. Contact and legal information">
        <p>
          Legal entity: {PLACEHOLDERS.entity} · Legal contact: {PLACEHOLDERS.email}. Questions about these
          terms can be sent to that address.
        </p>
      </Section>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600" data-testid="terms-counsel-notice">
        <p className="font-medium text-slate-800">Notice regarding this document</p>
        <p className="mt-1.5">
          This document is provided to describe the platform's current product structure and should be
          reviewed and adapted by qualified legal counsel before public launch. It is not legal advice.
        </p>
      </div>
    </LegalLayout>
  );
}
